import React, { useState, useEffect } from 'react';
import TopNavBar from '../components/common/TopNavBar';
import MockupQuizSession from '../components/quiz/MockupQuizSession';

const API = process.env.REACT_APP_API_URL;

const TYPE_META = {
  TOUCH_SELECT:          { label: 'Touch & Select',    color: '#f59e0b', emoji: '👆', bg: 'from-amber-50 to-orange-50', border: 'border-amber-200' },
  SPELLING_TYPING:       { label: 'Spell It Out',      color: '#8b5cf6', emoji: '✍️', bg: 'from-violet-50 to-purple-50', border: 'border-violet-200' },
  MULTIPLE_CHOICE_IMAGE: { label: 'Pick the Picture',  color: '#06b6d4', emoji: '🖼️', bg: 'from-cyan-50 to-sky-50', border: 'border-cyan-200' },
  MULTIPLE_CHOICE_TEXT:  { label: 'Choose the Answer',  color: '#3b82f6', emoji: '📝', bg: 'from-blue-50 to-indigo-50', border: 'border-blue-200' },
  STORY_CONSTRUCTION:    { label: 'Build the Story',   color: '#ec4899', emoji: '📖', bg: 'from-pink-50 to-rose-50', border: 'border-pink-200' },
};

const PAGE_LIMIT = 100;

const DebugQuiz = () => {
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionOpen, setSessionOpen] = useState(false);
  const [sessionQuizzes, setSessionQuizzes] = useState([]);
  const [showAnswer, setShowAnswer] = useState({});
  const [exerciseType, setExerciseType] = useState('');
  const [exerciseTypes, setExerciseTypes] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState({});

  useEffect(() => {
    fetch(`${API}/quiz-manage/exercise-types`)
      .then((r) => (r.ok ? r.json() : []))
      .then((types) => setExerciseTypes(types))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const fetchQuizzes = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({ skip: '0', limit: String(PAGE_LIMIT) });
        if (exerciseType) params.set('exercise_type', exerciseType);
        const response = await fetch(`${API}/quiz-manage/?${params}`);
        if (!response.ok) throw new Error('Failed to fetch quizzes');
        const json = await response.json();
        setQuizzes(json.data || []);
        setShowAnswer({});
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchQuizzes();
  }, [exerciseType]);

  const fetchDetail = async (id) => {
    setLoadingDetail((prev) => ({ ...prev, [id]: true }));
    try {
      const r = await fetch(`${API}/quiz-manage/${id}`);
      if (!r.ok) throw new Error('Failed to fetch quiz detail');
      const detail = await r.json();
      setQuizzes((prev) => prev.map((q) => (q.id === id ? { ...q, ...detail } : q)));
      return detail;
    } finally {
      setLoadingDetail((prev) => ({ ...prev, [id]: false }));
    }
  };

  const ensureDetail = async (quiz) => {
    if (quiz.answer_and_feedback) return quiz;
    return await fetchDetail(quiz.id);
  };

  const toggleAnswer = async (id) => {
    const quiz = quizzes.find((q) => q.id === id);
    if (quiz && !quiz.answer_and_feedback) {
      try { await fetchDetail(id); } catch (e) { setError(e.message); return; }
    }
    setShowAnswer((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const startAll = async () => {
    try {
      const full = await Promise.all(quizzes.map((q) => ensureDetail(q)));
      setSessionQuizzes(full);
      setSessionOpen(true);
    } catch (e) {
      setError(e.message);
    }
  };

  const startSingle = async (quiz) => {
    try {
      const full = await ensureDetail(quiz);
      setSessionQuizzes([full]);
      setSessionOpen(true);
    } catch (e) {
      setError(e.message);
    }
  };

  const STATIC_BASE = (API || '').replace(/\/api\/v\d+$/, '');
  const resolveUrl = (url) => (url && url.startsWith('/') ? `${STATIC_BASE}${url}` : url);
  const contextImage = (q) => {
    const raw = q.context?.context_image_url
      || q.context?.image_url
      || (q.context?.image_urls && q.context.image_urls[0]);
    return resolveUrl(raw) || null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-indigo-50/30 font-body">
      <TopNavBar />
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-black text-slate-800 font-headline mb-2">
            Quiz Debug
          </h1>
          <p className="text-slate-500 font-medium">
            Live quizzes from database. Filter by exercise type, test individually, or run a full session.
          </p>

          {/* Filter */}
          <div className="mt-4 flex items-center justify-center gap-3 flex-wrap">
            <label className="text-sm font-semibold text-slate-600">Exercise Type:</label>
            <select
              value={exerciseType}
              onChange={(e) => setExerciseType(e.target.value)}
              className="px-3 py-2 rounded-xl border border-slate-300 bg-white text-sm font-medium"
            >
              <option value="">All ({quizzes.length} shown)</option>
              {exerciseTypes.slice().sort().map((et) => (
                <option key={et} value={et}>{et}</option>
              ))}
            </select>
          </div>

          {!loading && !error && quizzes.length > 0 && (
            <button
              onClick={startAll}
              className="mt-4 px-8 py-3 rounded-2xl text-white text-lg font-black shadow-lg transition-all active:scale-95 hover:shadow-xl"
              style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}
            >
              ▶ Start Full Session ({quizzes.length} quizzes)
            </button>
          )}
        </div>

        {loading && (
          <div className="text-center py-12">
            <div className="inline-block w-10 h-10 border-4 border-indigo-200 border-t-indigo-500 rounded-full animate-spin" />
            <p className="mt-3 text-slate-500 font-medium">Loading mockup quizzes...</p>
          </div>
        )}
        {error && <p className="text-center text-red-500 font-bold">Error: {error}</p>}

        {/* Quiz cards */}
        {!loading && !error && (
          <div className="grid gap-5">
            {quizzes.map((quiz, idx) => {
              const meta = TYPE_META[quiz.quiz_type] || { label: quiz.quiz_type, color: '#6366f1', emoji: '❓', bg: 'from-slate-50 to-slate-100', border: 'border-slate-200' };
              return (
                <div
                  key={quiz.id}
                  className={`bg-gradient-to-br ${meta.bg} rounded-2xl border-2 ${meta.border} overflow-hidden shadow-sm hover:shadow-md transition-shadow`}
                >
                  <div className="flex flex-col md:flex-row">
                    {/* Image */}
                    {contextImage(quiz) && (
                      <div className="md:w-56 flex-shrink-0">
                        <img src={contextImage(quiz)} alt="preview" className="w-full h-40 md:h-full object-cover" />
                      </div>
                    )}

                    {/* Content */}
                    <div className="flex-1 p-5">
                      {/* Type badge + action */}
                      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-2xl">{meta.emoji}</span>
                          <span className="px-3 py-1 rounded-full text-sm font-black text-white" style={{ background: meta.color }}>
                            {meta.label}
                          </span>
                          <span className="px-2.5 py-1 rounded-lg bg-slate-800 text-white text-xs font-bold font-mono shadow-sm">
                            📚 {quiz.exercise_type}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => toggleAnswer(quiz.id)}
                            disabled={loadingDetail[quiz.id]}
                            className={`px-3 py-2 rounded-xl text-sm font-black shadow transition-all active:scale-95 hover:shadow-lg disabled:opacity-50 ${
                              showAnswer[quiz.id]
                                ? 'bg-green-500 text-white'
                                : 'bg-white text-slate-600 border border-slate-300'
                            }`}
                          >
                            {loadingDetail[quiz.id]
                              ? '⏳ Loading...'
                              : showAnswer[quiz.id]
                                ? '✓ Hide Answer'
                                : '? Answer'}
                          </button>
                          <button
                            onClick={() => startSingle(quiz)}
                            className="px-4 py-2 rounded-xl text-white text-sm font-black shadow transition-all active:scale-95 hover:shadow-lg"
                            style={{ background: meta.color }}
                          >
                            ▶ Test
                          </button>
                        </div>
                      </div>

                      {/* Story text */}
                      {quiz.context?.story_text && (
                        <p className="text-slate-600 text-sm leading-relaxed mb-2 whitespace-pre-line">
                          "{quiz.context.story_text}"
                        </p>
                      )}

                      {/* Question */}
                      <p className="text-lg font-bold text-slate-800 mb-2">
                        {quiz.question?.question_text
                          || quiz.question?.text
                          || (quiz.question?.text_before_blank !== undefined
                              ? `${quiz.question?.text_before_blank || ''}___${quiz.question?.text_after_blank || ''}`
                              : '—')}
                      </p>

                      {/* Options preview */}
                      {quiz.options && (
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {quiz.options.slice(0, 4).map((opt) => (
                            <span key={opt.id} className="flex items-center gap-1.5 px-2 py-1 bg-white/80 rounded-lg text-xs font-semibold text-slate-600 border border-white">
                              {opt.image_url && <img src={opt.image_url} alt="" className="w-6 h-6 rounded object-cover" />}
                              {opt.text}
                            </span>
                          ))}
                          {quiz.options.length > 4 && (
                            <span className="px-2 py-1 text-xs font-bold text-slate-400">+{quiz.options.length - 4}</span>
                          )}
                        </div>
                      )}

                      {/* Story images for STORY_CONSTRUCTION */}
                      {quiz.context?.image_urls && (
                        <div className="flex gap-1.5 mt-1">
                          {quiz.context.image_urls.map((url, i) => (
                            <img key={i} src={url} alt={`story ${i + 1}`} className="w-16 h-12 rounded-lg object-cover border border-white shadow-sm" />
                          ))}
                        </div>
                      )}

                      {/* Tags (from prompt_result.tags_data, loaded with detail) */}
                      {quiz.prompt_result?.tags_data && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(quiz.prompt_result.tags_data.vocabulary_tag_codes || []).map((t) => (
                            <span key={t} className="px-1.5 py-0.5 bg-blue-100/80 text-blue-600 rounded text-[10px] font-bold">{t}</span>
                          ))}
                          {(quiz.prompt_result.tags_data.grammar_tag_codes || []).map((t) => (
                            <span key={t} className="px-1.5 py-0.5 bg-yellow-100/80 text-yellow-600 rounded text-[10px] font-bold">{t}</span>
                          ))}
                          {(quiz.prompt_result.tags_data.hint_tag_codes || []).map((t) => (
                            <span key={t} className="px-1.5 py-0.5 bg-green-100/80 text-green-600 rounded text-[10px] font-bold">{t}</span>
                          ))}
                          {(quiz.prompt_result.tags_data.trap_tag_codes || []).map((t) => (
                            <span key={t} className="px-1.5 py-0.5 bg-red-100/80 text-red-600 rounded text-[10px] font-bold">{t}</span>
                          ))}
                        </div>
                      )}

                      {/* Answer panel */}
                      {showAnswer[quiz.id] && quiz.answer_and_feedback && (() => {
                        const af = quiz.answer_and_feedback;
                        const answerText = af.correct_answer_text || af.answer
                          || (af.answer_sequence || []).join(' → ');
                        const feedbackCorrect = af.teacher_feedback_correct || af.correct_feedback?.en;
                        const feedbackIncorrect = af.teacher_feedback_incorrect || af.incorrect_feedback?.en;
                        return (
                          <div className="mt-3 p-3 bg-white/90 rounded-xl border-2 border-green-300 space-y-2">
                            {/* Correct answer */}
                            <div className="flex items-center gap-2">
                              <span className="text-green-600 font-black text-sm">Answer:</span>
                              <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded-lg font-mono font-bold text-sm break-all">
                                {String(answerText)}
                              </span>
                            </div>
                            {/* Map answer to option text (when answer is option id) */}
                            {af.answer && quiz.options && (() => {
                              const opt = quiz.options.find((o) => o.id === af.answer);
                              return opt ? (
                                <div className="flex items-center gap-2">
                                  <span className="text-slate-500 font-bold text-xs">Option:</span>
                                  <span className="text-sm font-semibold text-slate-700">{opt.text}</span>
                                  {opt.image_url && <img src={opt.image_url} alt="" className="w-8 h-8 rounded object-cover" />}
                                </div>
                              ) : null;
                            })()}
                            {/* Sequence answer — show each step */}
                            {af.answer_sequence && quiz.options && (
                              <div className="space-y-1">
                                {af.answer_sequence.map((id, i) => {
                                  const opt = quiz.options.find((o) => o.id === id);
                                  return (
                                    <div key={id} className="flex items-center gap-2 text-xs">
                                      <span className="w-5 h-5 rounded-full bg-green-500 text-white flex items-center justify-center font-black text-[10px]">{i + 1}</span>
                                      <span className="font-semibold text-slate-700">{opt?.text || id}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            {/* Feedback */}
                            <div className="flex gap-2 mt-1 flex-wrap">
                              {feedbackCorrect && (
                                <div className="flex-1 min-w-[180px] text-[10px] bg-green-50 rounded-lg p-1.5">
                                  <span className="font-bold text-green-600">Correct:</span> {feedbackCorrect}
                                </div>
                              )}
                              {feedbackIncorrect && (
                                <div className="flex-1 min-w-[180px] text-[10px] bg-orange-50 rounded-lg p-1.5">
                                  <span className="font-bold text-orange-600">Wrong:</span> {feedbackIncorrect}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Quiz Session Overlay */}
      <MockupQuizSession
        quizzes={sessionQuizzes}
        open={sessionOpen}
        onClose={() => setSessionOpen(false)}
      />
    </div>
  );
};

export default DebugQuiz;
